/* WP-10 Me destination (§14.5.5): hub list plus agreements, notifications,
 * language, display, security, help, and legal document pages. Preferences
 * PATCH is a sensitive mutation → the shared step-up gate (§14.7). */
import { useEffect, useState, type ReactNode } from 'react';
import { Route, Routes, useNavigate, useParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { portalApi } from '../api/portal-api';
import type {
  AccountProfile,
  AgreementsResponse,
  LegalDoc,
  PortalPickupShop,
  PublicConfig,
} from '../api/types';
import { useSession } from '../auth/session-context';
import { isStepUpCancelled, useStepUp } from '../auth/step-up-context';
import { PrimaryButton, SecondaryButton } from '../components/primitives';
import { BottomSheet } from '../components/sheet';
import {
  BackIcon,
  BellIcon,
  DocumentIcon,
  GlobeIcon,
  HelpIcon,
  IdCardIcon,
  LegalIcon,
  LocationPinIcon,
  LogoutIcon,
  PaletteIcon,
  ShieldIcon,
} from '../icons';
import { getAppearance, lt, shopProfile, useAppearance } from '../appearance/appearance';
import { currentPortalLocale, PORTAL_LOCALES, setPortalLocale, type PortalLocale } from '../i18n';
import { formatDate } from '../utils/dates';
import { DISPLAY_PREFS, setDisplayPref } from '../utils/display-prefs';
import { errorText } from '../utils/error-text';
import { MarkdownBody } from '../utils/markdown';
import { SecurityRoutes } from './me-security-page';

export function MeRoutes() {
  return (
    <Routes>
      <Route index element={<MePage />} />
      <Route path="agreements" element={<AgreementsPage />} />
      <Route path="notifications" element={<NotificationsPage />} />
      <Route path="language" element={<LanguagePage />} />
      <Route path="display" element={<DisplayPage />} />
      <Route path="security/*" element={<SecurityRoutes />} />
      <Route path="help" element={<HelpPage backTo="/me" />} />
      <Route path="legal/:doc" element={<LegalPage backTo="/me" />} />
    </Routes>
  );
}

function RowLink({
  to,
  icon,
  title,
  sub,
  onClick,
  qualityId,
}: {
  to?: string;
  icon: ReactNode;
  title: string;
  sub?: string;
  onClick?: () => void;
  qualityId: string;
}) {
  const navigate = useNavigate();
  return (
    <button
      type="button"
      className="hp-listrow"
      onClick={onClick ?? (() => to && navigate(to))}
      data-quality-id={qualityId}
    >
      {icon}
      <span>
        {title}
        {sub ? <span className="hp-listrow__sub">{sub}</span> : null}
      </span>
      <span className="hp-listrow__chev">›</span>
    </button>
  );
}

function MePage() {
  const { t } = useTranslation();
  const { customer, signOut, signOutAll } = useSession();
  const { withStepUp } = useStepUp();
  const [account, setAccount] = useState<AccountProfile | null>(null);
  const [signOutOpen, setSignOutOpen] = useState(false);
  const [busy, setBusy] = useState(false);
  const [signOutAllError, setSignOutAllError] = useState<string | null>(null);

  useEffect(() => {
    portalApi
      .get<AccountProfile>('/account')
      .then(setAccount)
      .catch(() => {});
  }, []);

  /* F3-A — deliberate step-up cancellation stays quiet; a genuine
   * revocation failure surfaces an accessible message + retry. The copy
   * claims neither "all sessions revoked" nor "none revoked" — an
   * uncertain network outcome stays uncertain. Owner-scoped draft/intent
   * cleanup still runs only inside signOutAll after the confirmed POST,
   * so a failed attempt never discards a recoverable submission. */
  const doSignOutAll = async () => {
    if (busy) return; // no overlapping logout-all attempts
    setBusy(true);
    setSignOutAllError(null);
    try {
      // logout-all is step-up protected server-side; the gate re-checks it is you.
      await withStepUp(() => signOutAll());
    } catch (err) {
      if (!isStepUpCancelled(err)) setSignOutAllError(t('me.signOutAllFailed'));
    } finally {
      setBusy(false);
    }
  };

  return (
    <>
      <p className="hp-label">{account?.memberCodeMasked ?? customer?.memberCodeMasked}</p>
      <h1 className="hp-display-xl">{customer?.displayName ?? t('me.title')}</h1>

      <section className="hp-section">
        <div className="hp-rows">
          <RowLink
            to="/me/agreements"
            icon={<IdCardIcon size={20} />}
            title={t('me.agreements')}
            qualityId="portal.me.agreements"
          />
          <RowLink
            to="/me/notifications"
            icon={<BellIcon size={20} />}
            title={t('me.notifications')}
            qualityId="portal.me.notifications"
          />
          <RowLink
            to="/me/language"
            icon={<GlobeIcon size={20} />}
            title={t('me.language')}
            sub={t(`me.languageNames.${currentPortalLocale()}`)}
            qualityId="portal.me.language"
          />
          <RowLink
            to="/me/display"
            icon={<PaletteIcon size={20} />}
            title={t('me.display')}
            qualityId="portal.me.display"
          />
          <RowLink
            to="/me/security"
            icon={<ShieldIcon size={20} />}
            title={t('me.security')}
            qualityId="portal.me.security"
          />
          <RowLink
            to="/me/help"
            icon={<HelpIcon size={20} />}
            title={t('me.help')}
            qualityId="portal.me.help"
          />
          <RowLink
            to="/me/legal/terms"
            icon={<LegalIcon size={20} />}
            title={t('legal.terms')}
            qualityId="portal.me.terms"
          />
          <RowLink
            to="/me/legal/privacy"
            icon={<DocumentIcon size={20} />}
            title={t('legal.privacy')}
            qualityId="portal.me.privacy"
          />
        </div>
      </section>

      <section className="hp-section">
        <button
          type="button"
          className="hp-listrow"
          onClick={() => {
            setSignOutAllError(null); // reopening the sheet clears stale failure text
            setSignOutOpen(true);
          }}
          data-quality-id="portal.me.signOutOpen"
        >
          <LogoutIcon size={20} />
          <span>{t('me.signOut')}</span>
        </button>
      </section>

      <BottomSheet
        open={signOutOpen}
        onClose={() => setSignOutOpen(false)}
        title={t('me.signOutTitle')}
      >
        <div className="hp-stack">
          <PrimaryButton
            onClick={() => signOut()}
            loading={busy}
            data-quality-id="portal.me.signOut"
          >
            {t('me.signOutConfirm')}
          </PrimaryButton>
          <SecondaryButton
            danger
            onClick={doSignOutAll}
            loading={busy}
            data-quality-id="portal.me.signOutAll"
          >
            {t('me.signOutEverywhere')}
          </SecondaryButton>
          {signOutAllError ? (
            <p className="hp-danger-text" role="alert" data-quality-id="portal.me.signOutAllError">
              {signOutAllError}
            </p>
          ) : null}
        </div>
      </BottomSheet>
    </>
  );
}

export function SubPage({
  title,
  backTo = '/me',
  children,
}: {
  title: string;
  backTo?: string;
  children: ReactNode;
}) {
  const { t } = useTranslation();
  const navigate = useNavigate();
  return (
    <div className="hp-detail-narrow">
      <div className="hp-row hp-detail-head">
        <button
          type="button"
          className="hp-textlink"
          onClick={() => navigate(backTo)}
          aria-label={t('common.back')}
        >
          <BackIcon size={18} />
        </button>
        <h1 className="hp-display-xl">{title}</h1>
      </div>
      {children}
    </div>
  );
}

function AgreementsPage() {
  const { t } = useTranslation();
  const locale = currentPortalLocale();
  const [data, setData] = useState<AgreementsResponse | null>(null);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    portalApi
      .get<AgreementsResponse>('/contracts')
      .then(setData)
      .catch((err) => setError(errorText(err, t)));
  }, [t]);

  return (
    <SubPage title={t('me.agreementsPage.title')}>
      {error ? (
        <p className="hp-danger-text" role="alert">
          {error}
        </p>
      ) : null}
      <div className="hp-rows">
        {(data?.items ?? []).map((c) =>
          c.pdfAvailable ? (
            <a
              key={c.id}
              className="hp-listrow"
              href={portalApi.url(`/contracts/${c.id}/pdf`)}
              data-quality-id={`portal.agreement.${c.id}`}
            >
              <IdCardIcon size={20} />
              <span>
                {c.contractNumber}
                <span className="hp-listrow__sub">
                  {c.coverageEndDate
                    ? t('me.agreementsPage.coverageUntil', {
                        date: formatDate(c.coverageEndDate, locale),
                      })
                    : c.status}
                  {c.isCurrent ? ` · ${t('me.agreementsPage.active')}` : ''}
                </span>
              </span>
              <span className="hp-listrow__chev">›</span>
            </a>
          ) : (
            <div key={c.id} className="hp-listrow">
              <IdCardIcon size={20} />
              <span>
                {c.contractNumber}
                <span className="hp-listrow__sub">
                  {c.coverageEndDate
                    ? t('me.agreementsPage.coverageUntil', {
                        date: formatDate(c.coverageEndDate, locale),
                      })
                    : c.status}
                </span>
              </span>
            </div>
          ),
        )}
        {data && data.items.length === 0 ? (
          <p className="hp-tertiary">{t('me.agreementsPage.empty')}</p>
        ) : null}
      </div>
    </SubPage>
  );
}

function NotificationsPage() {
  const { t } = useTranslation();
  const { withStepUp } = useStepUp();
  const [account, setAccount] = useState<AccountProfile | null>(null);
  const [saving, setSaving] = useState(false);
  const [saved, setSaved] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    portalApi
      .get<AccountProfile>('/account')
      .then(setAccount)
      .catch((err) => setError(errorText(err, t)));
  }, [t]);

  const toggle = async (key: 'notifyEmail' | 'notifySms') => {
    if (!account) return;
    const next = !account[key];
    setSaving(true);
    setSaved(false);
    setError(null);
    try {
      // B2 PATCH is step-up protected — the shared sheet covers it.
      const res = await withStepUp(() =>
        portalApi.patch<Pick<AccountProfile, 'locale' | 'notifySms' | 'notifyEmail'>>(
          '/account/preferences',
          { [key]: next },
        ),
      );
      setAccount((prev) => (prev ? { ...prev, ...res } : prev));
      setSaved(true);
    } catch (err) {
      if (!isStepUpCancelled(err)) setError(errorText(err, t));
    } finally {
      setSaving(false);
    }
  };

  return (
    <SubPage title={t('me.notificationsPage.title')}>
      {error ? (
        <p className="hp-danger-text" role="alert">
          {error}
        </p>
      ) : null}
      {saved ? <p className="hp-muted">{t('me.notificationsPage.saved')}</p> : null}
      <div className="hp-rows">
        {(
          [
            ['notifyEmail', t('me.notificationsPage.email'), account?.emailMasked],
            ['notifySms', t('me.notificationsPage.sms'), account?.phoneMasked],
          ] as const
        ).map(([key, label, dest]) => (
          <button
            key={key}
            type="button"
            className="hp-listrow"
            role="switch"
            aria-checked={!!account?.[key]}
            disabled={saving || !dest}
            onClick={() => toggle(key)}
            data-quality-id={`portal.notifications.${key}`}
          >
            <BellIcon size={20} />
            <span>
              {label}
              {dest ? <span className="hp-listrow__sub">{dest}</span> : null}
            </span>
            <span className={`hp-switch ${account?.[key] ? 'hp-switch--on' : ''}`} aria-hidden />
          </button>
        ))}
      </div>
    </SubPage>
  );
}

function LanguagePage() {
  const { t, i18n } = useTranslation();
  const { withStepUp } = useStepUp();
  const [saving, setSaving] = useState(false);

  const choose = async (loc: PortalLocale) => {
    setSaving(true);
    setPortalLocale(loc);
    try {
      await withStepUp(() => portalApi.patch('/account/preferences', { locale: loc }));
    } catch {
      /* preference save is best-effort; locale already applied locally */
    } finally {
      setSaving(false);
    }
  };

  return (
    <SubPage title={t('me.languagePage.title')}>
      <div className="hp-rows" role="radiogroup" aria-label={t('me.languagePage.title')}>
        {PORTAL_LOCALES.map((loc) => (
          <button
            key={loc}
            type="button"
            className="hp-listrow"
            role="radio"
            aria-checked={i18n.language === loc}
            disabled={saving}
            onClick={() => choose(loc)}
            data-quality-id={`portal.language.${loc}`}
          >
            <GlobeIcon size={20} />
            <span>{t(`me.languageNames.${loc}`)}</span>
            <span className="hp-listrow__chev">{i18n.language === loc ? '●' : ''}</span>
          </button>
        ))}
      </div>
    </SubPage>
  );
}

function DisplayPage() {
  const { t } = useTranslation();
  const [large, setLarge] = useState(() =>
    document.documentElement.classList.contains('hp-large-text'),
  );
  const [contrast, setContrast] = useState(() =>
    document.documentElement.classList.contains('hp-contrast'),
  );

  return (
    <SubPage title={t('me.displayPage.title')}>
      <div className="hp-rows">
        <button
          type="button"
          className="hp-listrow"
          role="switch"
          aria-checked={large}
          onClick={() => {
            const next = !large;
            setLarge(next);
            setDisplayPref(DISPLAY_PREFS.largeText, next);
          }}
          data-quality-id="portal.display.largeText"
        >
          <PaletteIcon size={20} />
          <span>
            {t('me.displayPage.textSize')}
            <span className="hp-listrow__sub">
              {large ? t('me.displayPage.large') : t('me.displayPage.standard')}
            </span>
          </span>
          <span className={`hp-switch ${large ? 'hp-switch--on' : ''}`} aria-hidden />
        </button>
        <button
          type="button"
          className="hp-listrow"
          role="switch"
          aria-checked={contrast}
          onClick={() => {
            const next = !contrast;
            setContrast(next);
            setDisplayPref(DISPLAY_PREFS.contrast, next);
          }}
          data-quality-id="portal.display.contrast"
        >
          <PaletteIcon size={20} />
          <span>{t('me.displayPage.contrast')}</span>
          <span className={`hp-switch ${contrast ? 'hp-switch--on' : ''}`} aria-hidden />
        </button>
      </div>
    </SubPage>
  );
}

export function HelpPage({ backTo = '/me' }: { backTo?: string }) {
  const { t } = useTranslation();
  const locale = currentPortalLocale();
  useAppearance(); // re-render when the published bundle lands/changes
  const [config, setConfig] = useState<PublicConfig | null>(null);
  const [shops, setShops] = useState<PortalPickupShop[]>([]);
  const [faq, setFaq] = useState<Array<{ q: string; a: string }>>([]);

  useEffect(() => {
    portalApi
      .get<PublicConfig>('/public/config')
      .then(setConfig)
      .catch(() => setConfig({ locales: [], termsVersion: '', privacyNoticeVersion: '' }));
    portalApi
      .get<{ items: PortalPickupShop[] }>('/pickup-shops')
      .then((r) => setShops(r.items))
      .catch(() => {});
    setFaq(readFaq(locale));
  }, [locale]);

  return (
    <SubPage title={t('me.helpPage.title')} backTo={backTo}>
      <div className="hp-rows">
        {config?.supportEmail ? (
          <a className="hp-listrow" href={`mailto:${config.supportEmail}`}>
            <HelpIcon size={20} />
            <span>
              {t('me.helpPage.support')}
              <span className="hp-listrow__sub">{config.supportEmail}</span>
            </span>
          </a>
        ) : null}
        {config?.supportPhone ? (
          <a className="hp-listrow" href={`tel:${config.supportPhone}`}>
            <HelpIcon size={20} />
            <span>
              {t('me.helpPage.call')}
              <span className="hp-listrow__sub">{config.supportPhone}</span>
            </span>
          </a>
        ) : null}
        {shops.map((s) => {
          const profile = shopProfile(s.id);
          const address = lt(profile?.address, locale) ?? s.addressLines?.join(', ');
          const hours = lt(profile?.openingHours, locale) ?? s.openingHoursText;
          return (
            <div key={s.id} className="hp-listrow">
              <LocationPinIcon size={20} />
              <span>
                {lt(profile?.displayName, locale) ?? s.name}
                {address ? <span className="hp-listrow__sub">{address}</span> : null}
                {hours ? <span className="hp-listrow__sub">{hours}</span> : null}
              </span>
            </div>
          );
        })}
        {!config ? <div className="hp-skel hp-help-skel" /> : null}
      </div>
      {faq.length ? (
        <section className="hp-section">
          <h2 className="hp-label">{t('me.helpPage.faq')}</h2>
          {faq.map((f) => (
            <details key={f.q} className="hp-faq">
              <summary>{f.q}</summary>
              <MarkdownBody text={f.a} className="hp-muted" />
            </details>
          ))}
        </section>
      ) : null}
    </SubPage>
  );
}

/** FAQ entries live in the appearance document (question + answerMarkdown). */
function readFaq(locale: string): Array<{ q: string; a: string }> {
  const faq = getAppearance()?.document?.faq ?? [];
  return faq
    .map((f) => ({
      q: lt(f.question, locale) ?? '',
      a: lt(f.answerMarkdown, locale) ?? '',
    }))
    .filter((f) => f.q && f.a);
}

export function LegalPage({ backTo = '/me' }: { backTo?: string }) {
  const { t } = useTranslation();
  const { doc = 'terms' } = useParams<{ doc: string }>();
  const [content, setContent] = useState<LegalDoc | null>(null);
  const [error, setError] = useState<string | null>(null);
  const locale = currentPortalLocale();

  useEffect(() => {
    portalApi
      .get<LegalDoc>(`/public/legal/${doc}?locale=${locale}`)
      .then(setContent)
      .catch((err) => setError(errorText(err, t)));
  }, [doc, locale, t]);

  return (
    <SubPage
      title={content?.title ?? t(doc === 'privacy' ? 'legal.privacy' : 'legal.terms')}
      backTo={backTo}
    >
      {error ? (
        <p className="hp-danger-text" role="alert">
          {error}
        </p>
      ) : null}
      {content ? (
        <article className="hp-legal" data-quality-id={`portal.legal.${doc}`}>
          <MarkdownBody text={content.bodyMarkdown} className="hp-legal__body" />
          {content.version ? <p className="hp-caption">v{content.version}</p> : null}
        </article>
      ) : (
        <div className="hp-skel hp-legal-skel" />
      )}
    </SubPage>
  );
}
